export class Grid {
  private cells: boolean[];
  private _width: number;
  private _height: number;

  constructor(width: number, height: number) {
    this._width = width;
    this._height = height;
    this.cells = new Array(width * height).fill(false);
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get(x: number, y: number): boolean {
    if (x < 0 || x >= this._width || y < 0 || y >= this._height) {
      return false;
    }
    return this.cells[y * this._width + x];
  }

  set(x: number, y: number, value: boolean): void {
    if (x >= 0 && x < this._width && y >= 0 && y < this._height) {
      this.cells[y * this._width + x] = value;
    }
  }

  toggle(x: number, y: number): void {
    if (x >= 0 && x < this._width && y >= 0 && y < this._height) {
      const index = y * this._width + x;
      this.cells[index] = !this.cells[index];
    }
  }

  clear(): void {
    this.cells.fill(false);
  }

  randomize(density: number, seed?: number): void {
    let state = seed ?? Math.floor(Math.random() * 0xFFFFFFFF);

    for (let i = 0; i < this.cells.length; i++) {
      state = (state * 1103515245 + 12345) >>> 0;
      const value = ((state >>> 16) % 100) / 100;
      this.cells[i] = value < density;
    }
  }

  resize(width: number, height: number): void {
    if (width === this._width && height === this._height) return;

    const newCells = new Array(width * height).fill(false);
    const minW = Math.min(this._width, width);
    const minH = Math.min(this._height, height);

    for (let y = 0; y < minH; y++) {
      for (let x = 0; x < minW; x++) {
        if (this.cells[y * this._width + x]) {
          newCells[y * width + x] = true;
        }
      }
    }

    this._width = width;
    this._height = height;
    this.cells = newCells;
  }

  countNeighbors(x: number, y: number): number {
    let count = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < this._width && ny >= 0 && ny < this._height) {
          if (this.cells[ny * this._width + nx]) {
            count++;
          }
        }
      }
    }
    return count;
  }

  countAlive(): number {
    let count = 0;
    for (let i = 0; i < this.cells.length; i++) {
      if (this.cells[i]) count++;
    }
    return count;
  }

  copyFrom(other: Grid): void {
    this._width = other._width;
    this._height = other._height;
    this.cells = other.cells.slice();
  }
}
